import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, User2, Info, ArrowRight, FileSignature } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listing, ScenarioData } from '@/lib/index';
import { ScenarioCard } from '@/components/ScenarioCard';
import { ActionBar } from '@/components/ActionBar';
import { formatCurrency, calcNetProceeds } from '@/lib/index';

interface NetSheetEditorProps {
  listing: Listing;
  onMetaChange: (field: 'sellerName' | 'property', value: string) => void;
  onScenarioChange: (scenarioIdx: 0 | 1 | 2, field: keyof ScenarioData, value: number | string) => void;
  onDelete: () => void;
}

export function NetSheetEditor({ listing, onMetaChange, onScenarioChange, onDelete }: NetSheetEditorProps) {
  const nets = listing.scenarios.map(s => calcNetProceeds(s));
  const bestScenario = nets.indexOf(Math.max(...nets));

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-background">
      {/* Top Bar */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-foreground leading-tight">Seller Estimated Net Sheet</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                <ArrowRight size={10} />
                Scenario 1 is the master — all costs auto-sync to Scenarios 2 & 3
              </span>
              <span className="text-xs text-muted-foreground">Only the Sales Price is set independently per scenario</span>
            </div>
          </div>
          <ActionBar listing={listing} onDelete={onDelete} />
        </div>
      </div>

      {/* Listing Info */}
      <div className="px-6 py-4 bg-muted/30 border-b border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <User2 size={12} /> Seller Name(s)
            </Label>
            <Input
              value={listing.sellerName}
              onChange={e => onMetaChange('sellerName', e.target.value)}
              placeholder="Client name(s)"
              className="h-9 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <MapPin size={12} /> Property Address
            </Label>
            <Input
              value={listing.property}
              onChange={e => onMetaChange('property', e.target.value)}
              placeholder="Property address"
              className="h-9 text-sm"
            />
          </div>
        </div>

        {/* Legend + Best Scenario */}
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-input bg-background" />
            <span className="text-xs text-muted-foreground">Input field</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-green-200 bg-green-50" />
            <span className="text-xs text-muted-foreground">Auto-calculated</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-orange-300 bg-orange-50" />
            <span className="text-xs text-muted-foreground">Commission %</span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <Info size={11} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Best scenario: <strong className="text-primary">Scenario {bestScenario + 1}</strong>{' '}
              ({formatCurrency(nets[bestScenario])})
            </span>
          </div>
        </div>
      </div>

      {/* Scenarios Grid */}
      <div className="flex-1 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {listing.scenarios.map((scenario, i) => (
            <ScenarioCard
              key={i}
              index={i as 0 | 1 | 2}
              scenario={scenario}
              isMirroredFrom1={i > 0}
              onChange={(field, value) => onScenarioChange(i as 0 | 1 | 2, field, value)}
            />
          ))}
        </div>

        {/* Signature Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 border border-border rounded-xl bg-muted/30 p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <FileSignature size={16} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Signatures</p>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-1">
              Appears on printed / exported sheet — sign the printed copy
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Seller Signature', note: 'Sign on printed copy' },
              { label: 'Date', note: '' },
              { label: 'Agent Signature', note: 'Danielle Edwards' },
              { label: 'Date', note: '' },
            ].map((item, idx) => (
              <div key={`sig-top-${idx}`} className="space-y-1">
                <div className="border-b-2 border-border h-8 relative">
                  {item.note && (
                    <span className="absolute bottom-1 left-0 text-xs text-muted-foreground/50 italic">
                      {item.note}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4">
            {[
              { label: 'Seller Signature', note: 'Co-signer (if applicable)' },
              { label: 'Date', note: '' },
            ].map((item, idx) => (
              <div key={`sig-bot-${idx}`} className="space-y-1">
                <div className="border-b-2 border-border h-8 relative">
                  {item.note && (
                    <span className="absolute bottom-1 left-0 text-xs text-muted-foreground/50 italic">
                      {item.note}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-5 italic border-t border-border pt-3">
            NC REALTORS® MAKES NO REPRESENTATION AS TO THE LEGAL VALIDITY OR ADEQUACY OF ANY PROVISION OF THIS FORM IN ANY SPECIFIC TRANSACTION.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
